import { Navigate, Outlet, useLocation } from "react-router-dom";

import { ShieldX } from "lucide-react";
import { useTranslation } from "react-i18next";

import { parseApiError } from "@/api/errors";
import { ErrorState } from "@/components/shared/error-state";
import { FullScreenLoader } from "@/components/shared/full-screen-loader";
import { Button } from "@/components/ui/button";
import { useAdminMe, useLogout, useSessionBootstrap } from "@/hooks/use-session";
import { useAuthStore } from "@/store/auth";

/**
 * Himoyalangan marshrutlar: avval sessiya tiklanadi, keyin backend
 * foydalanuvchi admin ekanini tasdiqlaydi. Ruxsat UI'da emas, backend'da
 * (`AdminGuard`) — bu yerda faqat tushunarli ekran ko'rsatiladi.
 */
export function AuthGate() {
  const { t } = useTranslation();
  const location = useLocation();
  const bootstrapped = useSessionBootstrap();
  const accessToken = useAuthStore(s => s.accessToken);
  const me = useAdminMe(bootstrapped && !!accessToken);

  if (!bootstrapped) return <FullScreenLoader label={t("auth.checking")} />;
  if (!accessToken) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (me.isPending) return <FullScreenLoader label={t("auth.checking")} />;
  if (me.isError) {
    if (parseApiError(me.error).code === "ADMIN_ONLY") return <Forbidden />;
    return <ErrorState error={me.error} onRetry={() => void me.refetch()} />;
  }
  return <Outlet />;
}

function Forbidden() {
  const { t } = useTranslation();
  const user = useAuthStore(s => s.user);
  const logout = useLogout();
  return (
    <div className="flex min-h-svh items-center justify-center p-4">
      <div className="bg-card max-w-md space-y-3 rounded-3xl border p-8 text-center shadow-sm">
        <span className="bg-destructive/10 text-destructive animate-float mx-auto grid size-14 place-items-center rounded-2xl">
          <ShieldX className="size-7" aria-hidden />
        </span>
        <h1 className="text-xl font-semibold">{t("auth.forbiddenTitle")}</h1>
        <p className="text-muted-foreground text-sm">{t("auth.forbiddenText")}</p>
        {user ? (
          <p className="bg-muted rounded-md px-3 py-2 font-mono text-sm">
            {t("auth.yourId", { id: user.telegramUserId })}
          </p>
        ) : null}
        <Button variant="outline" onClick={() => void logout()}>
          {t("auth.otherAccount")}
        </Button>
      </div>
    </div>
  );
}
